// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the shapes the app layer works in.
//
// These deliberately mirror what the PQFORCE API actually returns rather than
// what the design mock held in `state`. Two differences carry the whole feature:
//
//   1. ⚠ `Todo` IS A CORE OBJECT TYPE, but its STATES are tenant configuration.
//      This comment used to say there was no Todo type in PQFORCE at all. That was
//      wrong, and it was corrected by measurement: `Todo` is one of the eight
//      hardcoded core object types in `lifecycleObjectTypesList()`, and
//      `NOTIFY_DEFAULT_OBJECT_TYPES` already treats it as a constant. So the item
//      type needs no configuration and none is offered.
//
//      What IS tenant configuration is everything inside it — including the
//      lifecycle's STATES, which the app shows exactly as the tenant configured
//      them: its names, its colours, its order. There is no fixed vocabulary of
//      states on this side and no mapping onto one in the configurator; every
//      state object PQFORCE returns carries its `category` and `sortIndex`, and
//      that is all the app reasons on. (A five-bucket model resolved from
//      configurator bindings did this until 2026-09-15.)
//
//   2. ⚠ TRANSITIONS COME FROM THE SERVER, NEVER FROM A TABLE HERE. The design
//      mock carries a `byRelation` lookup; that is a mock of the tenant's
//      lifecycle configuration, and the design's own footnote says so
//      ("Transitions come from your tenant's lifecycle configuration and your
//      relation to this TODO"). The real source is
//      `GET /LCY/Object/{type}/{id}/Transitions` — documented as "transitions
//      that are allowed for the current user" — or the
//      `possibleLifecycleTransitions` already carried on `ItemSummaries`.
//      Deriving them locally would show buttons the API then refuses, and hide
//      ones it would have allowed.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PQFORCE's own `lcy.LifecycleCategory`, as carried on every state.
 *
 * ⚠ A HINT, NOT A MEANING. It paints a chip whose state has no colour of its own and decides what
 * counts as still open (anything not CLOSED). It must not be read as more: measured on
 * develop.pqforce.com/dani, a state called "Blocked" carries PLANNING. `null` is a real value — a
 * state the tenant never categorised.
 */
export type LifecycleCategory = "NEW" | "PLANNING" | "ACTIVE" | "CLOSED";

export const LIFECYCLE_CATEGORIES: readonly LifecycleCategory[] = ["NEW", "PLANNING", "ACTIVE", "CLOSED"];

/** How this person relates to the TODO. Decides which transitions the API offers. */
export type TodoRelation = "assignee" | "owner";

/**
 * Which TODOs the list shows: assigned to me, owned by me — or ALL the TODOs this person may see,
 * including those they hold neither way (`relations` empty).
 */
export type ListScope = TodoRelation | "all";

/** Which axis the list groups on. */
export type TodoGrouping = "due" | "status" | "priority" | "project";

/** The order the list's grouping button steps through: deadline first, as the list opens. */
export const TODO_GROUPINGS: readonly TodoGrouping[] = ["due", "status", "priority", "project"];

/** The due buckets, in the order they are shown. Overdue first — it is the point. */
export type DueBucket = "Overdue" | "Today" | "This week" | "Later" | "None";

export const DUE_BUCKET_ORDER: readonly DueBucket[] = ["Overdue", "Today", "This week", "Later", "None"];

/**
 * A TODO's priority: one of the PQFORCE client's three fixed `prioMarker`s. Not a property and not free
 * text — the item's `marker` holds its key, and `TODO_PRIORITIES` (priorities.ts) is the whole list.
 */
export type Priority = "prio_low" | "prio_medium" | "prio_high";

/**
 * A lifecycle state's icon, as the server resolved it out of the portal's MDI font.
 *
 * `d` is the glyph outline in FONT coordinates (y up, baseline 0, 512 units per em), `w` its
 * advance width; `stateGlyph()` flips it. Null or absent when the state has no `mdi:` icon.
 * See mylibs/pqforce-mobile-icons.php.
 */
export interface StateIcon {
    d: string;
    w: number;
}

/**
 * One transition the API says this user may fire right now.
 *
 * `id` is what gets posted; `to` and `label` are for the button. Never construct
 * one of these locally — see the header.
 */
export interface LifecycleTransition {
    /** The transition id, for `POST /LCY/Object/{objType}/{objId}/Upgrade/{id}`. */
    id: string;
    /** The name of the state this leads to, as the tenant configured it. */
    to: string;
    /** The target state's id. */
    toId?: string;
    /** The target state's category and colour, so the sheet paints it as the list will. */
    toCategory?: LifecycleCategory | null;
    toColor?: string | null;
    toIcon?: StateIcon | null;
    /**
     * The transition's own name. ⚠ NEVER a move button's text — see transitionLabel(); read only as
     * a self-transition's comment placeholder (commentPlaceholder()).
     */
    label?: string;
    /**
     * `from.id === to.id`: a transition that does not move the item.
     *
     * These are real — one measured tenant models "Add log entry" as Init → Init —
     * and rendering one as "Move to Init" is nonsense.
     */
    selfTransition?: boolean;
}

/**
 * An activity entry. A plain comment and a state change are the same record —
 * a change simply also carries `from`/`to`, exactly as the design draws it, and
 * as `Upgrade`'s own `remark` parameter stores it.
 */
export interface TodoComment {
    who: string;
    /** Unix milliseconds. */
    ts: number;
    text: string;
    from?: string;
    to?: string;
    /**
     * Each endpoint's own category and colour, read off the state objects in the entry.
     *
     * ⚠ Optional, and never reverse-looked-up from `from`/`to` or from the todo's current state.
     * Painting a past step with today's state made every move show two identical chips. An endpoint
     * without either paints the neutral chip.
     */
    fromCategory?: LifecycleCategory | null;
    fromColor?: string | null;
    toCategory?: LifecycleCategory | null;
    toColor?: string | null;
    fromIcon?: StateIcon | null;
    toIcon?: StateIcon | null;
}

/**
 * What kind of PPM object a TODO is linked to.
 *
 * ⚠ `task` is PQFORCE's `Phase`. The API's spelling is mapped once, on the server, so the word
 * `Phase` never reaches a reader — the tenant's own relation is called "Belongs to task".
 */
export type PpmKind = "portfolio" | "project" | "task";

/**
 * The other things a TODO may be attached to, beside its one PPM link.
 *
 * ⚠ These are NOT alternatives to `PpmLink` and are never folded into it — a TODO may belong to a
 * task AND to a risk. Each reaches PPM indirectly: a risk and a project report belong to a project,
 * a portfolio report to a portfolio, which is what `project`/`portfolio` carry here.
 */
export type RelatedKind = "risk" | "projectReport" | "portfolioReport";

export interface RelatedLink {
    kind: RelatedKind;
    /**
     * ⚠ May legitimately be EMPTY. Neither `ProjectRisk` nor `ProjectPortfolioReport` is navigable
     * in PQFORCE, so a name is not always reachable — and a link nobody could name is still a link
     * the reader should be told about.
     */
    name: string;
    project: string;
    portfolio: string;
    /**
     * The linked OBJECT — its PQFORCE type (`ProjectRisk`, `ProjectReport`, …) and id. What a later
     * module opens, or preselects when the link is changed. Absent from an older server.
     */
    type?: string;
    id?: string;
}

export interface PpmLink {
    /**
     * ⚠ THE MOST SPECIFIC LEVEL WHOSE NAME THE SERVER COULD ACTUALLY RESOLVE, which is not always
     * the level the todo is linked at. A task's name lives in its project's plan and may be gone
     * (a deleted task), and PQFORCE offers no way to read a `Phase` by id at all — so a task link
     * whose name is unknown arrives as its project. That is a truthful downgrade, not a fallback
     * to be "fixed" on this side.
     */
    kind: PpmKind;
    /** Empty only when nothing in the chain could be named — the link still exists. */
    name: string;
    /** The project a TASK sits in. Empty otherwise, so a project link never prints itself twice. */
    project: string;
    /** The portfolio above the link. Empty on a portfolio link, for the same reason. */
    portfolio: string;
    /**
     * The PROJECT's id — of the linked project, or of the project a linked task sits in. What the list
     * groups by, so two projects sharing a name stay apart. Present even when no name could be read;
     * empty on a portfolio link, and absent from an older server.
     */
    projectId?: string;
    /**
     * The linked OBJECT — at the level the link was made (`Phase`, `Project`, `ProjectPortfolio`),
     * which `kind` is not always (that is the most specific level that could be NAMED). For a later
     * projects module to open it, or to preselect it when the link is changed. Absent from an older
     * server.
     */
    type?: string;
    id?: string;
}

/**
 * What this person may do to a TODO — PQFORCE's own answer for this person and this object, read
 * with the TODO when it is opened (`GET todos/{id}`). NOT the relation: an assignee may be allowed
 * to change a TODO, an owner may not. See `pqfMobileTodoPermissions()`.
 */
export interface TodoPermissions {
    /** Title, description, due date, priority — and reminding the assignees. */
    update: boolean;
    /** Its links: the assignees, the owner, and later the PPM link. */
    link: boolean;
    delete: boolean;
}

/**
 * One assignee of a TODO: a person, or a team or org unit — the Assignee relation accepts both.
 *
 * ⚠ `name` may be EMPTY: the server names at most a couple of dozen objects per request, and a
 * resource this user may not read has no name to give.
 */
export interface Assignee {
    /** The PQFORCE resource id — what an add, a remove and a nudge address. */
    id: string;
    name: string;
}

export interface Todo {
    id: string;
    /** The PI item type id this todo belongs to — needed to address it again. */
    itemType: string;
    /**
     * How this person reaches the TODO, for the app's own wording. OWNER when both hold.
     *
     * ⚠ Filter and gate on `relations`, not on this. A person can be owner AND assignee, which is
     * ordinary rather than an edge case, and this field cannot express it.
     */
    relation: TodoRelation;
    /** Every relation this person holds on it — 'assignee', 'owner', or both. */
    relations: TodoRelation[];
    /** The tenant's own word for the state. Shown to the user; never branched on. */
    status: string;
    /** The state's id. `""` from a server that predates it — group on the name then. */
    statusId: string;
    /** PQFORCE's category for the state. See `LifecycleCategory` for how little it may mean. */
    statusCategory: LifecycleCategory | null;
    /** The tenant's own colour for the state. The chip's background when it is a usable colour. */
    statusColor?: string | null;
    /**
     * The state's place in the tenant's lifecycle (`sortIndex`). What status groups and the home
     * tiles are ordered by. Null when the tenant did not say — never 0, which is the first place.
     */
    statusOrder: number | null;
    /** The state's own icon. Absent from an older server, null when the state has none. */
    statusIcon?: StateIcon | null;
    title: string;
    description: string;
    /**
     * The description as TEXT, for the owner's editor, and whether editing it there would lose
     * anything. The description is HTML and the phone has no rich-text editor — see
     * `pqfMobileDescriptionIsPlain()`, which decides this from the RAW value the tenant holds.
     *
     * ⚠ Both are absent from a server that predates the editor. `descriptionEditable` therefore
     * defaults to FALSE rather than true: an old server said nothing about the description's shape,
     * and offering to flatten one on that silence is the mistake this flag exists to prevent.
     */
    descriptionText?: string;
    descriptionEditable?: boolean;
    /**
     * The ONE link this TODO has into the PPM module, or null.
     *
     * ⚠ Never model this as separate project and task fields. A TODO reaches PPM through exactly
     * one relation — a portfolio, a project or a task — and PQFORCE's own client guarantees at most
     * one of the three exists. The levels above it come along as context, which is what `project`
     * and `portfolio` below are.
     */
    ppm: PpmLink | null;
    /** Risks and reports. Read-only, and shown separately from the one PPM link. */
    related: RelatedLink[];
    /** @deprecated Derived from `ppm` for one release, so an older bundle keeps its project line. */
    project: string;
    /** @deprecated Derived from `ppm`. An em dash when the link is not a task. */
    task: string;
    /**
     * EVERY assignee. ⚠ A TODO may have several — nothing in PQFORCE limits the relation to one —
     * so this is never reduced to a single name, and an empty list is a TODO nobody is assigned.
     */
    assignees: Assignee[];
    /**
     * Every owner's name, and the same joined into a sentence.
     *
     * ⚠ A TODO MAY HAVE SEVERAL OWNERS, exactly as it may have several assignees — measured on a
     * customer's tenant. `owner` is derived from `owners` and kept because an older bundle reads it;
     * `owners` is what a count is taken from and what says which owner is the person holding the
     * phone — by ID, never by name. An older SERVER sends `owner` alone, or `owners` as bare names,
     * so `toTodo()` accepts both and makes entries with an empty id, which match nobody.
     */
    owners: { id: string; name: string }[];
    owner: string;
    /**
     * The stored due date, `YYYY-MM-DD`, or `""` when the TODO has none.
     *
     * ⚠ THE ONLY DUE VALUE THAT IS STORED. The display label, the group bucket and the overdue flag
     * are all DERIVED from this and the current instant — see `dueFacts()`. A stored bucket or a
     * stored overdue boolean goes stale the moment the clock passes midnight, and this app lives in
     * a pocket across midnights.
     */
    dueISO: string;
    /**
     * ⚠ NULL when the item has no marker — most TODOs (measured: 9 of 12 on the dev
     * tenant). Not a neutral priority: a bar on every row would read as a deliberate one.
     */
    priority: Priority | null;
    comments: TodoComment[];
    /** What this user may fire, per the API. Empty is a real and common answer. */
    transitions: LifecycleTransition[];
    /**
     * True for a row from the "All TODOs" list, which carries no transitions and no activity: those
     * are read when the TODO is opened (`GET todos/{id}`). Absent on every other row.
     */
    partial?: boolean;
    /**
     * What this person may do to it, or ABSENT while that is not known yet — the lists do not carry
     * it (one request per row), so a TODO is read whole when it is opened, and its edit controls stay
     * inert until then. A transition needs none of this: `transitions` are already the moves PQFORCE
     * lets this person make.
     */
    permissions?: TodoPermissions;
    /** PQFORCE's own reference for the TODO, e.g. `T-12`. `""` when it has none. */
    code: string;
}

/** One rendered group in the list. */
export interface TodoGroup {
    /**
     * Stable identity for remembering whether the group is collapsed: `due:Overdue`,
     * `status:<state id>`, `priority:prio_high` / `priority:none`, or `project:id:<id>` /
     * `project:name:<name>` / `project:none`. Prefixed by the grouping so no two groupings' keys
     * can collide.
     */
    key: string;
    label: string;
    /** The priority a PRIORITY group is, for the heading's chip — the same chip its rows carry. */
    priority?: Priority;
    /** The bucket a DUE group is, for the heading's chip — coloured by how pressing it is. */
    due?: DueBucket;
    /**
     * The lifecycle state a STATUS group is, for the heading's state circle — the same circle its
     * rows draw. Only status groups carry one; every TODO in such a group is in this state.
     */
    state?: Pick<Todo, "status" | "statusCategory" | "statusColor" | "statusIcon">;
    /** The project a PROJECT group is, where its id is known — a new TODO can be created in it. */
    projectId?: string;
    count: number;
    items: Todo[];
}

/** How many todos are in one state, for a home tile. */
export interface StateCount {
    /** The tenant's name for the state. */
    label: string;
    count: number;
}

/** The two home tiles. */
export interface RelationSummary {
    total: number;
    overdue: number;
    /**
     * One entry per state that is still OPEN (category not CLOSED) and holds at least one todo, in
     * lifecycle order. States holding none are left out — a row of zeros for every state the tenant
     * has would crowd out the ones that matter.
     */
    states: StateCount[];
}

// ── onboarding ───────────────────────────────────────────────────────────────

/**
 * The onboarding machine.
 *
 * `discovering` and `validating` are transient; `failed` is NOT in the design's
 * own state list but the handoff calls the failure path out as required
 * ("Needs a real failure path"), so it is modelled here rather than bolted on.
 */
export type OnboardingStep = "url" | "discovering" | "options" | "login" | "sso" | "validating" | "done" | "failed";

/**
 * A sign-in method the tenant offered.
 *
 * ⚠ Built from the discovery response — "render one row per offered method; do
 * not hard-code two". A tenant may offer one, or five.
 */
export interface LoginMethod {
    id: string;
    kind: "sso" | "interactive";
    title: string;
    subtitle: string;
    /** Only one method is the preferred one; it wears the accent tint. */
    preferred?: boolean;
    /**
     * Whether this method can take an email and password THROUGH THE PORTAL.
     *
     * ⚠ Today nothing sets this, and that is not an oversight. The PQFORCE V2
     * API has NO username/password endpoint — all 46 `/ACM/` paths were checked
     * and none is a credential login. Credentials are collected by the tenant's
     * own authorization page (which may itself hand off to the customer's SAML
     * IdP), which is exactly why onboarding can promise "nothing is sent to us".
     *
     * So the designed email/password screen is reachable only if a tenant ever
     * gains such an endpoint and advertises it here. Routing the design's
     * "Sign in with email and password" row through the portal today would mean
     * posting a customer's password to a URL that cannot accept it — and would
     * break that promise on the way.
     */
    acceptsCredentials?: boolean;
}

export interface TenantDiscovery {
    host: string;
    /** The tenant's own display name, when it gives one. */
    displayName?: string;
    methods: LoginMethod[];
}

/** The three token-setup milestones. Each can fail on its own — and must say so. */
export type TokenStepState = "pending" | "active" | "done" | "failed";
